import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FirestoreProductSupplyDetail } from "reedy-data/models";
import { Badge } from "~/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "~/components/ui/dropdown-menu";
import { Text } from "~/components/text";
import { formatFirestoreTimestamp } from "~/shared/utils";
import { useProduct } from "~/contexts/product-context";
import { useProductAvailability } from "~/hooks/use-product-availability";
import { useFeatureFlags } from "~/contexts/feature-flag-context";
import { TruncatedText } from "~/components/truncated-text";

// Helper function to determine badge variant
const getAvailabilityBadgeVariant = (
  isActive: boolean,
): "secondary" | "error" => {
  return isActive ? "secondary" : "error";
};

// Helper function to format supply details for display
const formatSupplyDetails = (
  t: (key: string) => string,
  supplyDetails: FirestoreProductSupplyDetail[] = [],
): { supplier: string; availability: string }[] => {
  if (!supplyDetails || supplyDetails.length === 0) {
    return [];
  }

  return supplyDetails.map((detail) => {
    const supplier =
      detail.supplierName || t("productDetails:productSummary.unknownSupplier");
    const availability =
      detail.availability ||
      t("productDetails:productSummary.unknownAvailability");

    return {
      supplier,
      availability: availability.replace(/_/g, " "),
    };
  });
};

export const DetailsDropdown = () => {
  const { product } = useProduct();
  const { features } = useFeatureFlags();

  const { publisher, publishedAt, updatedAt, genre, supplyDetails } =
    product ?? {};
  const [firstGenre] = genre ?? [];

  const { t } = useTranslation(["productDetails"]);
  const { isProductActive } = useProductAvailability();

  // Determine if the product is active or inactive
  const isActive = isProductActive(supplyDetails);

  // Format supply details for display
  const formattedSupplyDetails = formatSupplyDetails(t, supplyDetails);
  const hasSupplyDetails = formattedSupplyDetails.length > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="details-dropdown-trigger">
        <div className="ui-btn-size-small py-1 px-1 cursor-pointer text-sm items-center font-medium whitespace-nowrap shrink-0 rounded-lg border border-secondary-200 bg-white text-secondary-600 shadow-xs hover:bg-secondary-25 hover:border-secondary-300 focus:shadow-outline-secondary">
          <ChevronDown size={16} />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <div className="p-2">
          <div className="flex items-center gap-1">
            <Text variant="label">
              {`${t("productDetails:productSummary.publisher")}:`}
            </Text>
            <Badge className="text-sm max-w-56 px-2">
              <TruncatedText text={publisher} />
            </Badge>
          </div>
          <div className="flex items-center gap-1 mt-2">
            <Text variant="label">
              {`${t("productDetails:productSummary.publishedDate")}:`}
            </Text>
            <Text>{formatFirestoreTimestamp(publishedAt)}</Text>
          </div>
          <div className="flex items-center gap-1 mt-2">
            <Text variant="label">
              {`${t("productDetails:productSummary.updatedDate")}:`}
            </Text>
            <Text>{formatFirestoreTimestamp(updatedAt)}</Text>
          </div>
          <div className="flex items-center gap-1 mt-2">
            <Text variant="label">
              {`${t("productDetails:productSummary.genre")}:`}
            </Text>
            <Text>{firstGenre}</Text>
          </div>
          {features.inactiveBooksFilter && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="p-0 mx-0 mt-2 hover:bg-transparent data-highlighted:bg-transparent">
                <div className="flex items-center gap-1">
                  <Text variant="label">
                    {`${t("productDetails:productSummary.status")}:`}
                  </Text>
                  <div className="flex items-center gap-1">
                    <Badge
                      variant={getAvailabilityBadgeVariant(isActive)}
                      className="text-sm"
                    >
                      {isActive
                        ? t("productDetails:productSummary.statusValues.active")
                        : t(
                            "productDetails:productSummary.statusValues.inactive",
                          )}
                    </Badge>
                  </div>
                </div>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="p-2 min-w-[220px]">
                {hasSupplyDetails ? (
                  <div className="pl-2 space-y-1.5 max-h-60 overflow-y-auto">
                    {formattedSupplyDetails.map((detail, index) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: values have no stable id and are not reordered
                      <Text as="div" variant="caption" key={index}>
                        <Text as="span" variant="labelSmall">
                          {detail.supplier}:
                        </Text>{" "}
                        <span>{detail.availability}</span>
                      </Text>
                    ))}
                  </div>
                ) : (
                  <Text variant="mutedItalic">
                    {t("productDetails:productSummary.noSupplyDetails")}
                  </Text>
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
